/**
 * Browser-compatible mirror of BE UserProfileHashHelper (PHP).
 * Keep in sync with backend normalization + SHA-256 (hex) output.
 */

export function normalizeString(value: string | null | undefined): string | null {
  if (value == null) {
    return null;
  }

  const normalized = value.trim().toLowerCase();

  if (normalized === "") {
    return null;
  }

  return normalized;
}

export function normalizedEmail(email: string | null | undefined): string | null {
  const normalized = normalizeString(email);

  if (normalized == null || !normalized.includes("@")) {
    return null;
  }

  return normalized;
}

export function isGmail(email: string | null | undefined): boolean | null {
  const normalizedEmailValue = normalizedEmail(email);

  if (normalizedEmailValue == null) {
    return null;
  }

  const domain = normalizedEmailValue.split("@", 2)[1] ?? "";

  return domain === "gmail.com" || domain === "googlemail.com";
}

export function normalizePhoneToE164(
  rawPhone: string | null | undefined,
  country: string | null | undefined
): string | null {
  const normalized = normalizeString(rawPhone);

  if (normalized == null) {
    return null;
  }

  const sanitized = normalized.replace(/[^\d+]/g, "");

  if (sanitized === "") {
    return null;
  }

  if (sanitized.startsWith("+")) {
    const digits = sanitized.slice(1);

    if (/^\d{8,15}$/.test(digits)) {
      return `+${digits}`;
    }

    return null;
  }

  if (/^\d{10}$/.test(sanitized) && (country ?? "").toLowerCase() === "us") {
    return `+1${sanitized}`;
  }

  return null;
}

/** User profile identifier aligned with BE: sha256(cleanedEmail($email)). */
export async function hashUserProfileEmail(
  email: string | null | undefined
): Promise<string | null> {
  return sha256(cleanedEmail(email));
}

export async function sha256(value: string | null | undefined): Promise<string | null> {
  const normalized = normalizeString(value);

  if (normalized == null) {
    return null;
  }

  const hashBuffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(normalized));
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function cleanedEmail(email: string | null | undefined): string | null {
  const normalizedEmailValue = normalizedEmail(email);

  if (normalizedEmailValue == null) {
    return null;
  }

  const atIndex = normalizedEmailValue.indexOf("@");
  const local = normalizedEmailValue.slice(0, atIndex);
  const domain = normalizedEmailValue.slice(atIndex + 1);
  const cleanLocal = local.split("+", 1)[0].replace(/\./g, "");

  if (cleanLocal === "" || domain === "") {
    return null;
  }

  return `${cleanLocal}@${domain}`;
}
